import { useNavigate } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cssbuy.wiki';

const guiaWebPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Guia Completo de Compras com CSSBuy Spreadsheet',
  description: 'Tudo que você precisa saber para começar a comprar da China com segurança, organização e economia usando o CSSBuy Spreadsheet.',
  url: `${siteUrl}/guia-de-compras`,
  inLanguage: 'pt-BR',
  isPartOf: {
    '@type': 'WebSite',
    name: 'CSSBuy.wiki',
    url: siteUrl,
  },
  about: {
    '@type': 'Thing',
    name: 'CSSBuy Spreadsheet',
    description: 'Planilha inteligente para organizar compras internacionais da China.',
  },
};

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'Como usar CSSBuy Spreadsheet para comprar da China',
  description: 'Guia passo a passo para começar a importar produtos da China com CSSBuy Spreadsheet.',
  step: [
    {
      '@type': 'HowToStep',
      position: 1,
      name: 'Acesse o CSSBuy Spreadsheet',
      text: 'O primeiro passo é acessar a planilha CSSBuy através do site principal. Faça uma cópia para sua conta pessoal para começar a usar imediatamente.',
    },
    {
      '@type': 'HowToStep',
      position: 2,
      name: 'Escolha seus Produtos por Categoria',
      text: 'Navegue pelas categorias disponíveis: Shoes, Hoodies, T-Shirts, Jackets, Pants, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes e outros.',
    },
    {
      '@type': 'HowToStep',
      position: 3,
      name: 'Insira os Dados na Planilha',
      text: 'Copie os links dos produtos que deseja comprar e cole na planilha. Preencha informações como tamanho, cor e quantidade.',
    },
    {
      '@type': 'HowToStep',
      position: 4,
      name: 'Otimize o Frete e Finalize',
      text: 'Use as fórmulas de cálculo da planilha para simular diferentes cenários de frete. Agrupe produtos similares para reduzir custos.',
    },
  ],
};

export default function GuiaDeCompras() {
  const navigate = useNavigate();

  return (
    <main className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(guiaWebPageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />
      {/* Navigation */}
      <nav className="sticky top-0 z-50 h-[72px] flex items-center px-4 md:px-10 bg-background-50/95 backdrop-blur-xl border-b border-background-200/70">
        <div className="w-full max-w-[900px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-1.5 text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer text-sm font-medium"
            >
              <i className="ri-arrow-left-line"></i>
              <span className="hidden sm:inline">Voltar</span>
            </button>
          </div>
          <button
            onClick={() => navigate('/')}
            className="text-foreground-900 font-heading text-lg font-bold tracking-tight whitespace-nowrap hover:opacity-80 transition-opacity cursor-pointer"
          >
            CSSBuy<span className="font-light italic text-foreground-600">.wiki</span>
          </button>
          <div className="w-16"></div>
        </div>
      </nav>

      {/* Header */}
      <header className="bg-background-100 border-b border-background-200/50">
        <div className="max-w-[900px] mx-auto px-4 md:px-10 py-14 md:py-20">
          <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
            Guia de Compras
          </span>
          <h1 className="font-heading text-foreground-900 text-3xl md:text-4xl lg:text-5xl font-black leading-tight mb-4">
            Guia Completo de Compras com CSSBuy Spreadsheet
          </h1>
          <p className="text-foreground-500 text-base md:text-lg leading-relaxed max-w-2xl">
            Tudo que você precisa saber para começar a comprar da China com segurança, organização e economia usando o CSSBuy Spreadsheet.
          </p>
        </div>
      </header>

      <article className="max-w-[900px] mx-auto px-4 md:px-10 py-10 md:py-16">
        {/* Step 1 */}
        <section className="mb-14">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
              <span className="text-background-50 font-bold text-lg">1</span>
            </div>
            <div>
              <h2 className="font-heading text-foreground-900 text-xl md:text-2xl font-bold mb-3">
                Acesse o CSSBuy Spreadsheet
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-3">
                O primeiro passo é acessar a planilha CSSBuy através do site principal. Faça uma cópia para sua conta pessoal para começar a usar imediatamente. A planilha já vem com todas as fórmulas e campos configurados.
              </p>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 text-accent-600 hover:text-accent-700 font-medium text-sm transition-colors cursor-pointer"
              >
                <span>Acessar planilha</span>
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </section>

        {/* Step 2 */}
        <section className="mb-14">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
              <span className="text-background-50 font-bold text-lg">2</span>
            </div>
            <div>
              <h2 className="font-heading text-foreground-900 text-xl md:text-2xl font-bold mb-3">
                Escolha seus Produtos por Categoria
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-3">
                Navegue pelas categorias disponíveis: Shoes, Hoodies, T-Shirts, Jackets, Pants, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes e outros. Cada categoria tem links diretos para o catálogo completo.
              </p>
            </div>
          </div>
        </section>

        {/* Step 3 */}
        <section className="mb-14">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
              <span className="text-background-50 font-bold text-lg">3</span>
            </div>
            <div>
              <h2 className="font-heading text-foreground-900 text-xl md:text-2xl font-bold mb-3">
                Insira os Dados na Planilha
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-3">
                Copie os links dos produtos que deseja comprar e cole na planilha. Preencha informações como tamanho, cor e quantidade. A planilha calcula automaticamente os custos estimados.
              </p>
            </div>
          </div>
        </section>

        {/* Step 4 */}
        <section className="mb-14">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
              <span className="text-background-50 font-bold text-lg">4</span>
            </div>
            <div>
              <h2 className="font-heading text-foreground-900 text-xl md:text-2xl font-bold mb-3">
                Otimize o Frete e Finalize
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-3">
                Use as fórmulas de cálculo da planilha para simular diferentes cenários de frete. Agrupe produtos similares para reduzir custos. Depois de escolher a melhor opção, finalize seu pedido com confiança.
              </p>
            </div>
          </div>
        </section>

        {/* Tips */}
        <section className="bg-background-100 rounded-2xl p-6 md:p-8 mb-14">
          <h3 className="font-heading text-foreground-900 text-xl font-bold mb-4 flex items-center gap-2">
            <i className="ri-lightbulb-line text-accent-500"></i>
            Dicas Importantes
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              'Sempre verifique as avaliações dos produtos antes de comprar',
              'Agrupe itens da mesma categoria para economizar no frete',
              'Mantenha sua planilha atualizada com o status de cada pedido',
              'Use a comunidade para tirar dúvidas e compartilhar experiências',
              'Compare preços entre diferentes vendedores na mesma categoria',
              'Acompanhe o rastreamento de cada pedido pela planilha',
            ].map((tip, i) => (
              <div key={i} className="flex items-start gap-2">
                <i className="ri-checkbox-circle-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                <span className="text-foreground-600 text-sm">{tip}</span>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="text-center bg-foreground-900 rounded-2xl p-10 md:p-14">
          <h2 className="font-heading text-background-50 text-2xl md:text-3xl font-bold mb-4">
            Pronto para começar?
          </h2>
          <p className="text-foreground-300 text-sm md:text-base mb-6 max-w-md mx-auto">
            Acesse o CSSBuy Spreadsheet agora e comece a comprar da China com o método mais eficiente do mercado.
          </p>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-background-50 text-foreground-900 text-sm md:text-base font-semibold hover:bg-background-100 transition-all duration-300 whitespace-nowrap cursor-pointer group"
          >
            <span>Buy It Now</span>
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
          </a>
        </div>

        {/* Back to home */}
        <div className="mt-10 text-center">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer text-sm"
          >
            <i className="ri-arrow-left-line"></i>
            <span>← Voltar para CSSBuy.wiki</span>
          </button>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-foreground-900 text-background-50">
        <div className="max-w-[900px] mx-auto px-4 md:px-10 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <button onClick={() => navigate('/')} className="text-lg font-heading font-bold tracking-tight hover:opacity-80 transition-opacity cursor-pointer">
              CSSBuy<span className="font-light italic text-foreground-400">.wiki</span>
            </button>
            <div className="flex items-center gap-5">
              <button onClick={() => navigate('/')} className="text-foreground-500 text-xs hover:text-background-50 transition-colors cursor-pointer">Termos</button>
              <button onClick={() => navigate('/')} className="text-foreground-500 text-xs hover:text-background-50 transition-colors cursor-pointer">Privacidade</button>
              <button onClick={() => navigate('/')} className="text-foreground-500 text-xs hover:text-background-50 transition-colors cursor-pointer">Sobre</button>
            </div>
          </div>
          <div className="mt-4 text-center">
            <p className="text-foreground-500 text-xs">
              &copy; {new Date().getFullYear()} CSSBuy.wiki. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
}